import React, { useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { ArrowLeft, ChevronLeft, ChevronRight, Images } from "lucide-react";
import { AuthContext } from "../../../../context/authContext";
import { convertDateToCT } from "../../../../utils/util";
import { downloadShipmentImage, getInboundShipmentLogs } from "../../../../reducers/warehouse.slice";
import { Button } from "../../../../components/ui/button";
import DataTable from "../../../../components/shared/DataTable";
import PageHeader from "../../../../components/shared/PageHeader";
import SearchBar from "../../../../components/shared/SearchBar";
import RefreshButton from "../../../../components/shared/RefreshButton";
import useMediaQuery from "../../../../components/shared/useMediaQuery";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../../../components/ui/dialog";

const WarehouseInboundItem = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  const { showToast } = useContext(AuthContext);
  const [showLoader, setShowLoader] = useState(false);
  const [inboundItemDetails, setInboundItemDetails] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [openImagesDialog, setOpenImagesDialog] = useState(false);
  const [selectedImages, setSelectedImages] = useState([]);
  const [currentImageIdx, setCurrentImageIdx] = useState(0);
  const isMobile = useMediaQuery("(max-width: 640px)");

  useEffect(() => { setCurrentImageIdx(0); }, [selectedImages]);

  const getLogs = () => {
    setShowLoader(true);
    dispatch(getInboundShipmentLogs(id)).then((res) => {
      if (res?.error && Object.keys(res?.error)?.length > 0) {
        setShowLoader(false);
        showToast(res?.error?.message, "error");
        return;
      }
      const formattedData = res?.payload?.map((item) => ({
        ...item,
        createdDate: convertDateToCT(item?.created_at, "MMMM D, YYYY hh:mm A"),
      }));
      setShowLoader(false);
      setInboundItemDetails(formattedData);
      setCurrentPage(0);
    });
  };

  useEffect(() => { getLogs(); }, []);

  const getImagesByID = (rowId) => {
    setShowLoader(true);
    dispatch(downloadShipmentImage(rowId)).then((res) => {
      if (res?.error && Object.keys(res?.error)?.length > 0) {
        setShowLoader(false);
        showToast(res?.error?.message, "error");
        return;
      }
      setSelectedImages(res?.payload);
      setOpenImagesDialog(true);
      setShowLoader(false);
    });
  };

  const filteredData = searchTerm.trim()
    ? inboundItemDetails.filter((item) =>
        item.upc && String(item.upc).toLowerCase().includes(searchTerm.toLowerCase())
      )
    : inboundItemDetails;

  const paginatedRows = filteredData.slice(
    currentPage * rowsPerPage,
    currentPage * rowsPerPage + rowsPerPage
  );

  const columns = [
    { key: "item_name", label: "Product Name", width: 200 },
    { key: "upc", label: "UPC", width: 130 },
    { key: "shipment_id", label: "Shipment ID", width: 120 },
    { key: "quantity", label: "Quantity", width: 90, align: "center" },
    { key: "createdDate", label: "Shipment Date", width: 200 },
    { key: "notes", label: "Notes", width: 150 },
    {
      key: "action",
      label: "",
      width: 60,
      align: "center",
      render: (row) =>
        row?.status === "DAMAGED" ? (
          <Button
            variant="ghost"
            size="icon-sm"
            title="View images"
            onClick={() => getImagesByID(row?.id)}
          >
            <Images size={16} />
          </Button>
        ) : null,
    },
  ];

  return (
    <div style={{ width: "100%" }}>
      <div
        style={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          justifyContent: "space-between",
          alignItems: isMobile ? "flex-start" : "center",
          gap: 12,
          marginBottom: 16,
        }}
      >
        <div>
          <button
            onClick={() => navigate(-1)}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              color: "#ffffff85",
              background: "none",
              border: "none",
              cursor: "pointer",
              padding: 0,
              fontSize: 14,
              marginBottom: 6,
            }}
          >
            <ArrowLeft size={14} /> Back
          </button>
          <PageHeader title="Inbound Item" subtitle="Search Results" />
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexShrink: 0, flexWrap: "wrap" }}>
          <RefreshButton onRefresh={getLogs} spinning={showLoader} />
          <SearchBar
            placeholder="Search by UPC…"
            searchTerm={searchTerm}
            setSearchTerm={(v) => { setSearchTerm(v); setCurrentPage(0); }}
          />
        </div>
      </div>

      <DataTable
        columns={columns}
        rows={paginatedRows}
        totalRows={filteredData.length}
        currentPage={currentPage}
        rowsPerPage={rowsPerPage}
        onPageChange={(n) => setCurrentPage(n)}
        onRowsPerPageChange={(n) => { setRowsPerPage(n); setCurrentPage(0); }}
        loading={showLoader}
        minWidth={800}
      />

      {/* Image carousel dialog */}
      <Dialog open={openImagesDialog} onOpenChange={() => setOpenImagesDialog(false)}>
        <DialogContent style={{ maxWidth: isMobile ? "95vw" : 520 }}>
          <DialogHeader>
            <DialogTitle>Images</DialogTitle>
          </DialogHeader>
          {selectedImages.length > 0 ? (
            <>
              <div
                style={{
                  position: "relative",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  minHeight: isMobile ? 180 : 300,
                  padding: "0 44px",
                }}
              >
                <button
                  onClick={() =>
                    setCurrentImageIdx((prev) =>
                      prev > 0 ? prev - 1 : selectedImages.length - 1
                    )
                  }
                  disabled={selectedImages.length <= 1}
                  style={{
                    position: "absolute",
                    left: 0,
                    background: "none",
                    border: "none",
                    cursor: selectedImages.length <= 1 ? "default" : "pointer",
                    padding: 4,
                    color: "white",
                    opacity: selectedImages.length <= 1 ? 0.3 : 1,
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <ChevronLeft size={28} />
                </button>
                <img
                  src={`data:${selectedImages[currentImageIdx].content_type};base64,${selectedImages[currentImageIdx].document_bytes}`}
                  alt={`Item image ${currentImageIdx + 1}`}
                  style={{
                    maxWidth: "100%",
                    maxHeight: isMobile ? 180 : 350,
                    borderRadius: 8,
                    objectFit: "contain",
                    display: "block",
                  }}
                />
                <button
                  onClick={() =>
                    setCurrentImageIdx((prev) =>
                      prev < selectedImages.length - 1 ? prev + 1 : 0
                    )
                  }
                  disabled={selectedImages.length <= 1}
                  style={{
                    position: "absolute",
                    right: 0,
                    background: "none",
                    border: "none",
                    cursor: selectedImages.length <= 1 ? "default" : "pointer",
                    padding: 4,
                    color: "white",
                    opacity: selectedImages.length <= 1 ? 0.3 : 1,
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <ChevronRight size={28} />
                </button>
              </div>
              <div style={{ textAlign: "center", marginTop: 8, fontSize: 13, color: "#888" }}>
                {currentImageIdx + 1} / {selectedImages.length}
              </div>
            </>
          ) : (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: isMobile ? 180 : 300,
                color: "#888",
              }}
            >
              No image available
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default WarehouseInboundItem;
